import { useState } from 'react';
import { ExternalLink, Play, Video } from 'lucide-react';
import { formatDuration, getYouTubeVideoId } from '../services/videoService';
import { formatINR } from '../utils/format';
import './VideoCard.css';

const youTubeThumb = id => `https://img.youtube.com/vi/${id}/hqdefault.jpg`;

/**
 * Video tile. The whole card is clickable through one control:
 * - `href` set: a link that opens in a new tab (e.g. non-YouTube videos);
 * - otherwise a button that calls `onClick` (e.g. open the player);
 * - neither: a plain, non-interactive tile.
 */
const VideoCard = ({ video, onClick, href }) => {
  const youTubeId = getYouTubeVideoId(video?.videoUrl);
  const fallback = youTubeId ? youTubeThumb(youTubeId) : null;
  const [src, setSrc] = useState(video?.thumbnailUrl || fallback);

  const handleImageError = () => {
    // Saved thumbnail broken: try YouTube's own; after that, show the placeholder.
    setSrc(current => (fallback && current !== fallback ? fallback : null));
  };

  const tech = Array.isArray(video?.technologyStack)
    ? video.technologyStack.filter(Boolean)
    : [];
  const title = video?.title || 'Untitled video';
  const meta = [video?.category, video?.level].filter(Boolean);
  const duration = formatDuration(video?.duration);
  const price = video?.product?.price;
  const featureCount = Array.isArray(video?.features) ? video.features.length : 0;

  const label = (
    <>
      <span className="vc__clamp">{title}</span>
      {href && <span className="vc-sr"> (opens in a new tab)</span>}
    </>
  );

  return (
    <article className="vc">
      <div className="vc__media">
        {src ? (
          <img
            src={src}
            alt=""
            loading="lazy"
            decoding="async"
            onError={handleImageError}
          />
        ) : (
          <span className="vc__placeholder" aria-hidden="true">
            <Video />
          </span>
        )}
        {(href || onClick) && (
          <span className="vc__play" aria-hidden="true">
            {href ? <ExternalLink /> : <Play />}
          </span>
        )}
        {duration && (
          <span className="vc__duration">
            <span className="vc-sr">Length </span>
            {duration}
          </span>
        )}
      </div>

      <div className="vc__body">
        {meta.length > 0 && <p className="vc__meta">{meta.join(' · ')}</p>}
        <h3 className="vc__title">
          {href && (
            <a
              className="vc__action"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {label}
            </a>
          )}
          {!href && onClick && (
            <button type="button" className="vc__action" onClick={onClick}>
              {label}
            </button>
          )}
          {!href && !onClick && label}
        </h3>
        {video?.description && <p className="vc__desc">{video.description}</p>}
        {tech.length > 0 && (
          <ul className="vc__tags" aria-label="Technologies">
            {tech.slice(0, 3).map(item => (
              <li key={item} className="pk-badge">
                {item}
              </li>
            ))}
            {tech.length > 3 && (
              <li className="pk-badge">+{tech.length - 3}</li>
            )}
          </ul>
        )}
        {(price != null || featureCount > 0) && (
          <p className="vc__foot">
            {price != null && <span className="vc__price">{formatINR(price)}</span>}
            {featureCount > 0 && (
              <span className="vc__count">
                {featureCount} {featureCount === 1 ? 'feature' : 'features'}
              </span>
            )}
          </p>
        )}
      </div>
    </article>
  );
};

export default VideoCard;
